import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { Params } from '@angular/router';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { ApplicationContextStore, AuthService, NamedRoutes, RouterService, TokenService, TranslationService, URLService } from '@raptor-fe/services';
import { firstValueFrom } from 'rxjs';
import { LaunchFailure, LaunchQueryParams, toLaunchFailure } from '../launch.constants';

export interface LaunchState {
    failure: LaunchFailure | null;
}

export const LaunchStore = signalStore(
    withState<LaunchState>({
        failure: null,
    }),
    withMethods(
        (
            store,
            authService = inject(AuthService),
            tokenService = inject(TokenService),
            urlService = inject(URLService),
            routerService = inject(RouterService),
            translationService = inject(TranslationService),
            applicationContextStore = inject(ApplicationContextStore),
            location = inject(Location),
        ) => {
            /**
             * The branch the user chose in Blue. Handed to the welcome page so it adopts that branch
             * instead of asking. Read from the launch claim rather than branch_id, which holds the
             * user's saved Raptor branch.
             */
            function launchedBranchId(accessToken: string | null | undefined): string | undefined {
                if (!accessToken) {
                    return undefined;
                }

                const openToken = authService.getDecodedAccessToken(accessToken);

                if (!openToken?.launch_branch_id) {
                    return undefined;
                }

                return openToken.launch_branch_id;
            }

            async function enterWelcome(accessToken: string | null | undefined): Promise<void> {
                await firstValueFrom(translationService.getDictionary()).catch((error: unknown) => {
                    console.error('Could not load the translation dictionary after a Blue launch.', error);
                });

                await routerService.navigateAndReplaceUrl(NamedRoutes.welcome(undefined, launchedBranchId(accessToken)));
            }

            return {
                /**
                 * Signs in with the one-time launch code through the ordinary login, which the
                 * federation API recognises and redeems in place of a password.
                 *
                 * The code is stripped from the address before it is spent, because signing in can
                 * reload the page when the session's country differs from the stored one, and a reload
                 * must not replay a code that is already redeemed. That reload lands here with no code
                 * but with the new session stored, so a stored token resumes into the welcome page.
                 *
                 * A launch never continues a stored session: a leftover token would ride the bootstrap
                 * requests and could end this visit at a sign-in its user cannot use. Display mode is
                 * set to regular explicitly, so a web-view flag left by an earlier customer link cannot
                 * hide the navigation from a staff user. The server url is saved before login so every
                 * later request carries it to the federation API.
                 */
                async launch(params: Params): Promise<void> {
                    const reportedFailure = params[LaunchQueryParams.ERROR] as string | undefined;

                    if (reportedFailure) {
                        patchState(store, { failure: toLaunchFailure(reportedFailure) });
                        return;
                    }

                    const code = params[LaunchQueryParams.CODE] as string | undefined;

                    if (!code) {
                        const storedToken = tokenService.getToken();

                        if (storedToken) {
                            await enterWelcome(storedToken);
                            return;
                        }

                        patchState(store, { failure: LaunchFailure.ExchangeFailed });
                        return;
                    }

                    const baseUrl = params[LaunchQueryParams.BASE_URL] as string | undefined;
                    const username = params[LaunchQueryParams.USERNAME] as string | undefined;

                    if (!baseUrl || !username) {
                        patchState(store, { failure: LaunchFailure.ExchangeFailed });
                        return;
                    }

                    location.replaceState(NamedRoutes.launch().path);

                    tokenService.removeToken();
                    tokenService.removeRefreshToken();
                    applicationContextStore.setIsLoggedIn(false);
                    void applicationContextStore.setIsWebViewMode(false);
                    urlService.saveURL(baseUrl);

                    try {
                        const session = await firstValueFrom(authService.login({ username, password: code }, baseUrl));

                        await enterWelcome(session.access_token);
                    } catch (error) {
                        console.error('The Blue launch code could not be exchanged for a session.', error);
                        patchState(store, { failure: LaunchFailure.ExchangeFailed });
                    }
                },
            };
        },
    ),
);
